import { Exports_GetDefinitionDetial } from "data/endpoints";
import React, { useEffect } from "react";
import { useQuery } from "data/hooks/useQuery";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import ExportDefinitionDetailDTO from "features/exports/dtos/ExportDefinitionDetailDTO";
import PageCard from "features/shared/components/card/PageCard";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import { getQueryParams } from "features/shared/helpers/queryParams";
import { useExportDynamicForm } from "features/exports/hooks/useExportDynamicForm";
import { useForm } from "features/shared/forms/hooks/useForm";
import DynamicForm from "features/shared/forms/dynamicForm/DynamicForm";
import RolesChecker from "features/customer/components/RolesChecker";
import { useTranslation } from "react-i18next";
import ExportParamsDTO from "features/exports/dtos/ExportParamsDTO";
import { useExport } from "features/exports/hooks/useExport";
import _ from "lodash";
import {
  customerParamName,
  exportFileTypeKey,
  getDataDescriptors,
} from "features/exports/helpers/exportFormHelper";
import { useHistory } from "react-router-dom";
import { isEmpty } from "features/shared/helpers/objects";
import { Exports_ExportDetailSection } from "features/shared/components/loaders/promiseLoadingAreas";
import PromiseLoadingSpinner from "features/shared/components/loaders/PromiseLoadingSpinner";

type Props = {
  exportDefinitionId: string;
};

const ExportDetailSection = ({ exportDefinitionId }: Props) => {
  // translation
  const { t: tLabels, i18n } = useTranslation("exports\\exports");

  // router
  const history = useHistory();

  // store
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  // side-effects
  // fetch-data
  const { data: exportDefinition, loadStatus } =
    useQuery<ExportDefinitionDetailDTO>(
      {
        url: Exports_GetDefinitionDetial,
        routeParams: getRouteParams("exportDefinitionId", exportDefinitionId),
        params: getQueryParams("CustomerId", customer.customerId!),
      },
      {},
      [customer.customerId]
    );

  // after language change redirect to exports list (there are different exports for different languages)
  useEffect(() => {
    if (!isEmpty(exportDefinition)) {
      history.push("/exports");  
    }
  }, [i18n.language]);

  // form hook
  const formHandler = useForm({});
  const { values, valideteForm, setFormValue, getFormValue } = { ...formHandler };

  // exports hooks
  const { formJson } = useExportDynamicForm({
    exportDefinition: exportDefinition,
    setFormValue: setFormValue,
    paramNamesToSkip: [customerParamName],
  });
  const { exportHandler } = useExport({
    showInfoDialog: true,
    loadingArea: Exports_ExportDetailSection,
  });

  // handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    if (valideteForm()) {
      const defParams = exportDefinition.parameters ?? [];
      let exportParams = getDataDescriptors(values, defParams);

      let params: ExportParamsDTO = {
        fileTypeId: getFormValue(exportFileTypeKey),
        parameters: exportParams,
      };

      await exportHandler(exportDefinitionId, params);
    }
  };

  // render
  return (
    <NoContentWrapper loadStatus={loadStatus} data={exportDefinition}>
      <form onSubmit={submitFormHandler}>
        <PageCard title={exportDefinition.name!} bodyClassName="pt-4 px-4">
          <div className="row no-gutters mx-n2">
            <DynamicForm
              formJson={formJson}
              fieldProps={{ formHandler: formHandler }}
            />
          </div>
        </PageCard>
        <RolesChecker enabledRoles="Contributor">
          <div className="position-relative d-inline-flex">
            <PromiseLoadingSpinner 
              area={Exports_ExportDetailSection} 
              spinnerSizeInRem={1.5}  
            />
            <button type="submit" className="btn btn-primary px-3 py-2">
              {tLabels("exportDetailSection.export")}
            </button>
          </div>
          
        </RolesChecker>
      </form>
    </NoContentWrapper>
  );
};

export default ExportDetailSection;
